/**
 * Prévia do texto padronizado com as variáveis resolvidas
 *
 * A prévia é POST sem id porque ela precisa funcionar para texto ainda não
 * salvo, enquanto a pessoa digita. O escopo é a empresa; informar o setor muda
 * a resolução de `{{escopo.*}}`, que é o único jeito de conferir texto escrito
 * pensando em setor.
 *
 * A empresa segue o texto: texto de empresa específica usa a empresa dele, e
 * só texto global pede uma empresa de exemplo — escolha de conferência, que
 * não entra no que é salvo. Quando não dá para gerar, a prévia diz o que falta
 * em vez de ficar em branco.
 *
 * @version 2026.1.0
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Typography,
} from '@mui/material';
import { Edit as EditIcon } from '@mui/icons-material';
import { CompanySelectField } from '../../../components/forms';
import { departmentService } from '../../../services/departmentService';
import { standardTextService } from '../../../services/standardTextService';
import { useCompanyList } from '../../../stores/companyStore';
import { getApiErrorMessage, isApiError } from '../../../types/common';
import { Company } from '../../../types';
import { Department } from '../../../types/department';
import { StandardTextPreviewResult } from '../../../types/standardText';

const DEBOUNCE_PADRAO_MS = 600;

/**
 * O que o status HTTP significa para quem está escrevendo o texto. A mensagem
 * do backend vem logo abaixo, e é ela que diz qual chave não compilou ou qual
 * empresa foi negada.
 */
const CAUSA_POR_STATUS: Record<number, string> = {
  400: 'O texto não compila — confira as chaves {{ }}',
  403: 'Esta empresa está fora do seu acesso',
  404: 'Empresa ou setor não encontrado',
};

interface FalhaDaPrevia {
  causa: string;
  detalhe: string;
}

const falhaDaPrevia = (erro: unknown): FalhaDaPrevia => {
  const status = isApiError(erro) ? erro.status : undefined;
  const causa = status === undefined ? undefined : CAUSA_POR_STATUS[status];
  return {
    causa: causa ?? 'Não foi possível gerar a prévia',
    detalhe: getApiErrorMessage(erro, 'O servidor não explicou o motivo.'),
  };
};

interface StandardTextPreviewProps {
  content: string;
  /** Empresa do texto quando ele não é global; a prévia usa essa e não pergunta. */
  companyId?: number | null;
  isGlobal: boolean;
  sectionKey?: string | null;
  /** Leva de volta para a aba Editar quando falta algo que se resolve lá. */
  onEditar?: () => void;
  debounceMs?: number;
}

export const StandardTextPreview: React.FC<StandardTextPreviewProps> = ({
  content,
  companyId = null,
  isGlobal,
  sectionKey,
  onEditar,
  debounceMs = DEBOUNCE_PADRAO_MS,
}) => {
  const [empresaExemplo, setEmpresaExemplo] = useState('');
  const [departmentId, setDepartmentId] = useState('');
  const [departments, setDepartments] = useState<Department[]>([]);
  const [avisoDeSetor, setAvisoDeSetor] = useState<string | null>(null);
  const [result, setResult] = useState<StandardTextPreviewResult | null>(null);
  const [carregando, setCarregando] = useState(false);
  const [falha, setFalha] = useState<FalhaDaPrevia | null>(null);

  const { companies, isLoading: carregandoEmpresas, loadCompanies } = useCompanyList();

  // O backend aceita `sectionKey` no corpo, mas nenhuma variável depende dela:
  // deixá-la fora das dependências evita refazer a prévia a cada troca de seção.
  const sectionKeyRef = useRef(sectionKey);
  useEffect(() => {
    sectionKeyRef.current = sectionKey;
  }, [sectionKey]);

  const empresaEfetiva = isGlobal ? empresaExemplo : companyId ? String(companyId) : '';
  const conteudoVazio = content.trim().length === 0;
  const podeGerar = empresaEfetiva !== '' && !conteudoVazio;

  const handleEmpresaExemploChange = useCallback((value: string) => {
    setEmpresaExemplo(value);
    setDepartmentId('');
    setDepartments([]);
    setAvisoDeSetor(null);
    setResult(null);
    setFalha(null);
  }, []);

  // O nome da empresa do texto identifica de quem são os dados da prévia.
  useEffect(() => {
    if (companies.length === 0 && !carregandoEmpresas) loadCompanies();
  }, [companies.length, carregandoEmpresas, loadCompanies]);

  const nomeDaEmpresa = useMemo(() => {
    const empresa = companies.find((item: Company) => String(item.id) === empresaEfetiva);
    if (!empresa) return null;
    return empresa.tradeName || empresa.legalName;
  }, [companies, empresaEfetiva]);

  useEffect(() => {
    if (!empresaEfetiva) return;

    let cancelado = false;
    departmentService
      .getDepartmentsByCompanyId(empresaEfetiva)
      .then((lista) => {
        if (cancelado) return;
        setDepartments(lista ?? []);
        setAvisoDeSetor(null);
      })
      .catch((err: unknown) => {
        if (cancelado) return;
        setDepartments([]);
        // Listar setor exige `department.read`. Quem só administra textos não
        // tem: sumir com o campo faz parecer que a empresa não tem setor.
        setAvisoDeSetor(
          isApiError(err) && err.status === 403
            ? 'Você não tem permissão para ver os setores desta empresa, então a prévia usa a empresa inteira. Peça a um administrador se precisar conferir o texto por setor.'
            : `Não foi possível carregar os setores desta empresa (${getApiErrorMessage(err, 'erro desconhecido')}). A prévia usa a empresa inteira.`
        );
      });

    return () => {
      cancelado = true;
    };
  }, [empresaEfetiva]);

  useEffect(() => {
    if (!podeGerar) {
      // Conteúdo apagado ou empresa trocada no meio do caminho: sem isto o
      // "gerando" da requisição abandonada ficaria girando para sempre.
      setCarregando(false);
      setResult(null);
      setFalha(null);
      return;
    }

    let cancelado = false;
    // Ligado antes do debounce: o intervalo de espera era área em branco, e
    // uma prévia antiga na tela passava por atual enquanto a nova não chegava.
    setCarregando(true);
    setFalha(null);

    const timer = setTimeout(() => {
      standardTextService
        .previewContent({
          content,
          companyId: Number(empresaEfetiva),
          ...(departmentId ? { departmentId: Number(departmentId) } : {}),
          ...(sectionKeyRef.current ? { sectionKey: sectionKeyRef.current } : {}),
        })
        .then((previa) => {
          if (!cancelado) setResult(previa);
        })
        .catch((err: unknown) => {
          if (cancelado) return;
          setResult(null);
          setFalha(falhaDaPrevia(err));
        })
        .finally(() => {
          if (!cancelado) setCarregando(false);
        });
    }, debounceMs);

    return () => {
      cancelado = true;
      clearTimeout(timer);
    };
  }, [content, empresaEfetiva, departmentId, debounceMs, podeGerar]);

  const voltarParaEditar = onEditar && (
    <Button size="small" variant="outlined" startIcon={<EditIcon />} onClick={onEditar}>
      Voltar para Editar
    </Button>
  );

  return (
    <Box>
      {isGlobal ? (
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid size={{ xs: 12, md: 6 }}>
            <CompanySelectField
              value={empresaExemplo}
              onChange={handleEmpresaExemploChange}
              required={false}
              hideOnEdit={false}
              label="Empresa de exemplo"
            />
          </Grid>
          <Grid size={12}>
            <Typography variant="body2" color="text.secondary">
              Este texto vale para todas as empresas. A empresa de exemplo serve só para
              conferir como ele fica resolvido — ela não é salva no texto.
              {nomeDaEmpresa !== null && ` A prévia abaixo usa os dados de ${nomeDaEmpresa}.`}
            </Typography>
          </Grid>
        </Grid>
      ) : (
        empresaEfetiva !== '' && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {`Prévia com os dados de ${nomeDaEmpresa ?? 'a empresa deste texto'}.`}
          </Typography>
        )
      )}

      {empresaEfetiva !== '' && departments.length > 0 && (
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid size={{ xs: 12, md: 6 }}>
            <FormControl fullWidth>
              <InputLabel id="standard-text-preview-department-label">Setor</InputLabel>
              <Select
                value={departmentId}
                labelId="standard-text-preview-department-label"
                label="Setor"
                onChange={(event) => setDepartmentId(String(event.target.value))}
              >
                <MenuItem value="">Empresa inteira</MenuItem>
                {departments.map((department) => (
                  <MenuItem key={department.id} value={department.id}>
                    {department.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      )}

      {avisoDeSetor && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {avisoDeSetor}
        </Alert>
      )}

      {conteudoVazio && (
        <Stack spacing={1} alignItems="flex-start">
          <Typography variant="body2" color="text.secondary">
            Escreva o conteúdo do texto na aba Editar para ver aqui como ele fica com os
            dados da empresa.
          </Typography>
          {voltarParaEditar}
        </Stack>
      )}

      {!conteudoVazio && empresaEfetiva === '' && !isGlobal && (
        <Stack spacing={1} alignItems="flex-start">
          <Typography variant="body2" color="text.secondary">
            Escolha a empresa deste texto na aba Editar: a prévia resolve as variáveis com
            os dados dela.
          </Typography>
          {voltarParaEditar}
        </Stack>
      )}

      {!conteudoVazio && empresaEfetiva === '' && isGlobal && (
        <Typography variant="body2" color="text.secondary">
          Escolha uma empresa de exemplo acima para ver o texto resolvido.
        </Typography>
      )}

      {carregando && !result && (
        <Stack direction="row" spacing={1} alignItems="center">
          <CircularProgress size={20} />
          <Typography variant="body2" color="text.secondary">
            Gerando a prévia…
          </Typography>
        </Stack>
      )}

      {falha && (
        <Alert severity="error" sx={{ mb: 2 }}>
          <AlertTitle>{falha.causa}</AlertTitle>
          {falha.detalhe}
        </Alert>
      )}

      {result && podeGerar && (
        <>
          {carregando && (
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
              <CircularProgress size={16} />
              <Typography variant="body2" color="text.secondary">
                Atualizando a prévia com o que você acabou de escrever…
              </Typography>
            </Stack>
          )}

          {/* O HTML vem sanitizado do backend (`renderRichText`), o mesmo caminho da emissão.
              A tabela repete a borda e o fundo de cabeçalho de `.doc-tabela` nos templates de
              documento (#412): sem isso a prévia mostraria uma tabela sem borda nenhuma e
              mentiria sobre o PDF, que é justamente o que ela existe para conferir. */}
          <Box
            sx={{
              border: 1,
              borderColor: 'divider',
              borderRadius: 1,
              p: 2,
              mb: 2,
              opacity: carregando ? 0.5 : 1,
              '& table': { width: '100%', borderCollapse: 'collapse', mb: 1 },
              '& th, & td': {
                border: '1px solid #000',
                padding: '2px 3px',
                textAlign: 'left',
                verticalAlign: 'top',
              },
              '& thead th': { backgroundColor: '#eee', fontWeight: 'bold' },
            }}
            dangerouslySetInnerHTML={{ __html: result.html }}
          />

          {result.variaveisNaoResolvidas.length > 0 && (
            <Alert severity="warning">
              <Typography variant="body2" gutterBottom>
                Estas variáveis ficaram sem valor nesta empresa — confira o cadastro dela ou
                revise o texto:
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {result.variaveisNaoResolvidas.map((token) => (
                  <Chip key={token} size="small" label={token} />
                ))}
              </Stack>
            </Alert>
          )}
        </>
      )}
    </Box>
  );
};

export default StandardTextPreview;
